import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackButton } from '../../components/BackButton';
import { FormPageShell } from '../../components/DataTablePanel';
import { TableIconButton } from '../../components/TableIconAction';
import {
  Button,
  Callout,
  Card,
  DateInput,
  Label,
  LoadingSpinner,
  PlusIcon,
  SaveIcon,
  Select,
  TextInput,
  Textarea,
  XIcon,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CompanyExpenseCategory } from '../../types/company-expense';
import { formatMoney } from '../../utils/format';

const VAT_RATES = ['0', '6', '13', '24'];

type PaymentDraft = {
  key: string;
  paymentDate: string;
  netAmount: string;
  vatAmount: string;
};

type FormState = {
  categoryId: string;
  expenseDate: string;
  dueDate: string;
  vatDueDate: string;
  vendorName: string;
  documentNo: string;
  description: string;
  notes: string;
  amount: string;
  vatRate: string;
  type: 'INVOICE' | 'CASH';
  payments: PaymentDraft[];
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function emptyPayment(): PaymentDraft {
  return {
    key: `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    paymentDate: todayIso(),
    netAmount: '0',
    vatAmount: '0',
  };
}

function emptyForm(): FormState {
  return {
    categoryId: '',
    expenseDate: todayIso(),
    dueDate: todayIso(),
    vatDueDate: '',
    vendorName: '',
    documentNo: '',
    description: '',
    notes: '',
    amount: '',
    vatRate: '24',
    type: 'INVOICE',
    payments: [],
  };
}

function calcVat(amount: string, vatRate: string): string {
  const net = Number(amount);
  const rate = Number(vatRate);
  if (!Number.isFinite(net) || !Number.isFinite(rate)) return '0.00';
  return ((net * rate) / 100).toFixed(2);
}

function toApiAmount(value: string): string {
  const n = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
}

export function CompanyExpenseFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const labels = t().companyExpenses;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [categories, setCategories] = useState<CompanyExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vatAmount = useMemo(
    () => calcVat(form.amount, form.vatRate),
    [form.amount, form.vatRate],
  );
  const gross = useMemo(() => {
    const net = Number(form.amount) || 0;
    const vat = Number(vatAmount) || 0;
    return (net + vat).toFixed(2);
  }, [form.amount, vatAmount]);

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      setLoading(true);
      try {
        const categoryResult = await api.getCompanyExpenseCategories(true);
        if (cancelled) return;
        setCategories(categoryResult);

        if (isEdit && id) {
          const expense = await api.getCompanyExpense(Number(id));
          if (cancelled) return;
          if (!categoryResult.some((c) => c.id === expense.category.id)) {
            setCategories([
              ...categoryResult,
              {
                id: expense.category.id,
                name: expense.category.name,
                active: expense.category.active,
              },
            ]);
          }
          setForm({
            categoryId: String(expense.category.id),
            expenseDate: expense.expenseDate.slice(0, 10),
            dueDate: expense.dueDate.slice(0, 10),
            vatDueDate: expense.vatDueDate
              ? expense.vatDueDate.slice(0, 10)
              : '',
            vendorName: expense.vendorName ?? '',
            documentNo: expense.documentNo ?? '',
            description: expense.description ?? '',
            notes: expense.notes ?? '',
            amount: expense.amount.net,
            vatRate: expense.vatRate,
            type: expense.type,
            payments: expense.payments.map((p) => ({
              key: `p-${p.id}`,
              paymentDate: p.paymentDate.slice(0, 10),
              netAmount: p.netAmount,
              vatAmount: p.vatAmount,
            })),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!form.categoryId) {
      setError(labels.requiredCategory);
      return;
    }
    if (!form.expenseDate) {
      setError(labels.requiredExpenseDate);
      return;
    }
    if (!form.dueDate) {
      setError(labels.requiredDueDate);
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      setError(labels.requiredAmount);
      return;
    }

    const payload = {
      categoryId: Number(form.categoryId),
      expenseDate: form.expenseDate,
      dueDate: form.dueDate,
      vatDueDate: form.vatDueDate || null,
      vendorName: form.vendorName.trim() || null,
      documentNo: form.documentNo.trim() || null,
      description: form.description.trim() || null,
      notes: form.notes.trim() || null,
      amount: toApiAmount(form.amount),
      vatRate: form.vatRate,
      type: form.type,
      payments: form.payments.map((p, index) => ({
        paymentDate: p.paymentDate,
        netAmount: toApiAmount(p.netAmount),
        vatAmount: toApiAmount(p.vatAmount),
        sortOrder: index,
      })),
    };

    setSaving(true);
    try {
      if (isEdit && id) {
        await api.updateCompanyExpense(Number(id), payload);
      } else {
        await api.createCompanyExpense(payload);
      }
      navigate('/company-expenses');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().toasts.saveError);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <FormPageShell>
        <LoadingSpinner message={t().common.loading} />
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <BackButton fallback="/company-expenses" />
          <h1 className="mt-2 text-lg font-semibold text-ink">
            {isEdit ? labels.edit : labels.new}
          </h1>
        </div>
      </div>

      {error ? (
        <Callout variant="danger" className="mb-4">
          {error}
        </Callout>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card className="space-y-4 p-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <Label required>{labels.category}</Label>
              <Select
                value={form.categoryId}
                placeholder={labels.selectCategory}
                options={categories.map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, categoryId: e.target.value }))
                }
              />
            </div>
            <div>
              <Label required>{labels.expenseDate}</Label>
              <DateInput
                value={form.expenseDate}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, expenseDate: e.target.value }))
                }
              />
            </div>
            <div>
              <Label required>{labels.dueDate}</Label>
              <DateInput
                value={form.dueDate}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, dueDate: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{labels.vatDueDate}</Label>
              <DateInput
                value={form.vatDueDate}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, vatDueDate: e.target.value }))
                }
              />
              <p className="mt-1 text-xs text-ink-faint">
                {labels.vatDueDateHint}
              </p>
            </div>
            <div>
              <Label>{labels.vendorName}</Label>
              <TextInput
                value={form.vendorName}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, vendorName: e.target.value }))
                }
                showClearButton={false}
              />
            </div>
            <div>
              <Label>{labels.documentNo}</Label>
              <TextInput
                value={form.documentNo}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, documentNo: e.target.value }))
                }
                showClearButton={false}
              />
            </div>
            <div>
              <Label required>{labels.amount}</Label>
              <TextInput
                value={form.amount}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, amount: e.target.value }))
                }
                showClearButton={false}
                inputMode="decimal"
              />
            </div>
            <div>
              <Label required>{labels.vatRate}</Label>
              <Select
                value={form.vatRate}
                options={VAT_RATES.map((rate) => ({
                  value: rate,
                  label: `${rate}%`,
                }))}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, vatRate: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{labels.vatAmount}</Label>
              <TextInput value={vatAmount} readOnly showClearButton={false} />
            </div>
            <div>
              <Label>{labels.gross}</Label>
              <TextInput
                value={formatMoney(gross)}
                readOnly
                showClearButton={false}
              />
            </div>
            <div>
              <Label required>{labels.type}</Label>
              <Select
                value={form.type}
                options={[
                  { value: 'INVOICE', label: t().financial.invoice },
                  { value: 'CASH', label: t().financial.cash },
                ]}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    type: e.target.value as 'INVOICE' | 'CASH',
                  }))
                }
              />
            </div>
          </div>
          <div>
            <Label>{labels.description}</Label>
            <TextInput
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              showClearButton={false}
            />
          </div>
          <div>
            <Label>{labels.notes}</Label>
            <Textarea
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              rows={3}
            />
          </div>
        </Card>

        <Card className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink">
              {labels.paymentsSection}
            </h2>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  payments: [...prev.payments, emptyPayment()],
                }))
              }
            >
              <PlusIcon className="h-3.5 w-3.5" />
              {labels.addPayment}
            </Button>
          </div>

          {form.payments.length === 0 ? (
            <p className="text-sm text-ink-faint">{labels.noPayments}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-ink-faint">
                    <th className="px-2 py-1">{labels.paymentDate}</th>
                    <th className="px-2 py-1 text-right">{labels.paymentNet}</th>
                    <th className="px-2 py-1 text-right">{labels.paymentVat}</th>
                    <th className="px-2 py-1" />
                  </tr>
                </thead>
                <tbody>
                  {form.payments.map((payment, index) => (
                    <tr key={payment.key} className="border-t border-line">
                      <td className="px-2 py-1">
                        <DateInput
                          value={payment.paymentDate}
                          onChange={(e) =>
                            setForm((prev) => {
                              const payments = [...prev.payments];
                              payments[index] = {
                                ...payments[index],
                                paymentDate: e.target.value,
                              };
                              return { ...prev, payments };
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-1">
                        <TextInput
                          value={payment.netAmount}
                          onChange={(e) =>
                            setForm((prev) => {
                              const payments = [...prev.payments];
                              payments[index] = {
                                ...payments[index],
                                netAmount: e.target.value,
                              };
                              return { ...prev, payments };
                            })
                          }
                          showClearButton={false}
                          inputMode="decimal"
                          className="text-right"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <TextInput
                          value={payment.vatAmount}
                          onChange={(e) =>
                            setForm((prev) => {
                              const payments = [...prev.payments];
                              payments[index] = {
                                ...payments[index],
                                vatAmount: e.target.value,
                              };
                              return { ...prev, payments };
                            })
                          }
                          showClearButton={false}
                          inputMode="decimal"
                          className="text-right"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <TableIconButton
                          label={labels.removePayment}
                          variant="muted"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              payments: prev.payments.filter(
                                (_, i) => i !== index,
                              ),
                            }))
                          }
                        >
                          <XIcon className="h-4 w-4" />
                        </TableIconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/company-expenses')}
          >
            <XIcon className="h-4 w-4" />
            {t().common.cancel}
          </Button>
          <Button type="submit" loading={saving}>
            <SaveIcon className="h-4 w-4" />
            {t().common.save}
          </Button>
        </div>
      </form>
    </FormPageShell>
  );
}
